import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";

interface FeedItem {
  id: string;
  icon: string;
  description: string;
  created_at: string;
}

interface ActivityFeedProps {
  tripId: string;
}

export default function ActivityFeed({ tripId }: ActivityFeedProps) {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tripId) return;

    const load = async () => {
      setLoading(true);

      const [expensesRes, messagesRes, todosRes, programRes] = await Promise.all([
        supabase
          .from("trip_expenses")
          .select("id, description, amount, currency, created_at, profiles(first_name)")
          .eq("trip_id", tripId)
          .order("created_at", { ascending: false })
          .limit(3),
        supabase
          .from("chat_messages")
          .select("id, content, created_at, profiles(first_name)")
          .eq("trip_id", tripId)
          .order("created_at", { ascending: false })
          .limit(3),
        supabase
          .from("trip_todos")
          .select("id, title, created_at, profiles(first_name)")
          .eq("trip_id", tripId)
          .order("created_at", { ascending: false })
          .limit(2),
        supabase
          .from("trip_program_items")
          .select("id, title, created_at, profiles(first_name)")
          .eq("trip_id", tripId)
          .order("created_at", { ascending: false })
          .limit(2),
      ]);

      const feedItems: FeedItem[] = [];

      (expensesRes.data ?? []).forEach((e: any) => {
        const name = e.profiles?.first_name ?? "Jemand";
        const amt = e.amount ? `${e.currency ?? "CHF"} ${Number(e.amount).toFixed(2)}` : "";
        feedItems.push({
          id: `expense-${e.id}`,
          icon: "💸",
          description: `${name} hat eine Ausgabe erfasst${e.description ? ` – ${e.description}` : ""}${amt ? ` (${amt})` : ""}`,
          created_at: e.created_at,
        });
      });

      (messagesRes.data ?? []).forEach((m: any) => {
        const name = m.profiles?.first_name ?? "Jemand";
        const preview = m.content?.length > 40 ? m.content.slice(0, 40) + "…" : m.content;
        feedItems.push({
          id: `msg-${m.id}`,
          icon: "💬",
          description: `${name} im Chat: „${preview}"`,
          created_at: m.created_at,
        });
      });

      (todosRes.data ?? []).forEach((t: any) => {
        const name = t.profiles?.first_name ?? "Jemand";
        feedItems.push({
          id: `todo-${t.id}`,
          icon: "✅",
          description: `${name} hat eine Aufgabe hinzugefügt: ${t.title}`,
          created_at: t.created_at,
        });
      });

      (programRes.data ?? []).forEach((p: any) => {
        const name = p.profiles?.first_name ?? "Jemand";
        feedItems.push({
          id: `prog-${p.id}`,
          icon: "📅",
          description: `${name} hat einen Programmpunkt hinzugefügt: ${p.title}`,
          created_at: p.created_at,
        });
      });

      // Sortieren nach Datum (neuestes zuerst), max. 8 Einträge
      feedItems.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setItems(feedItems.slice(0, 8));
      setLoading(false);
    };

    load();
  }, [tripId]);

  if (loading) {
    return (
      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-4">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
          Zuletzt passiert
        </p>
        <p className="text-xs text-slate-500 animate-pulse">Wird geladen…</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-4">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
          Zuletzt passiert
        </p>
        <p className="text-sm text-slate-500">Noch keine Aktivität.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] overflow-hidden">
      <div className="px-4 py-3 border-b border-[#2a3347]">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
          Zuletzt passiert
        </p>
      </div>
      <div className="divide-y divide-[#2a3347]">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-3 p-4 hover:bg-[#1e2639] transition-colors">
            <span className="text-lg flex-shrink-0">{item.icon}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-300 truncate">{item.description}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: de })}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
